import React, { useRef, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  CssBaseline,
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { usePageTranslation } from '../i18n/hooks';
import { useTracking } from '../hooks/useTracking';
import ConcordChat from '../components/ConcordChat';
import { clearCache } from '../utils/axios';
import Footer from '../components/Footer';

const NavButton = ({ children, ...props }) => {
  const theme = useTheme();
  return (
    <Button
      {...props}
      sx={{
        px: 2,
        py: 1,
        borderRadius: '8px',
        textTransform: 'none',
        fontSize: '0.9rem',
        fontWeight: 500,
        letterSpacing: '0.3px',
        color: 'text.primary',
        whiteSpace: 'nowrap',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-2px)',
          color: 'primary.main',
          backgroundColor: 'rgba(13, 148, 136, 0.06)',
          boxShadow: '0 2px 8px rgba(13, 148, 136, 0.12)',
        },
        '&.active': {
          color: 'primary.main',
          '&::after': {
            width: '100%',
          }
        }
      }}
    >
      {children}
    </Button>
  );
};

const MainLayout = ({ children }) => {
  const { t } = usePageTranslation('nav');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Check current page type
  const isProjectsPage = location.pathname === '/projects' || location.pathname.match(/^\/projects\/[^/]+$/);
  const isInterviewPage = location.pathname.includes('/interview') || location.pathname.includes('/audio-interview');
  const isHomePage = location.pathname === '/' || location.pathname === '/home';
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const token = localStorage.getItem('token');

  useTracking();

  // Clear expired tokens on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        const decoded = jwtDecode(storedToken);
        if (decoded.exp * 1000 < Date.now()) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, []);

  const getUsername = () => {
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded.sub;
    } catch (error) {
      console.error('Invalid token:', error);
      localStorage.removeItem('token');
      return null;
    }
  };

  const username = getUsername();

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('username');
    clearCache();
    handleClose();
    navigate('/login');
  };

  // Primary nav items (always visible)
  const menuItems = [
    { label: t('projects'), path: '/projects' },
  ];

  const renderMobileMenu = () => (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      PaperProps={{
        sx: {
          background: '#FFFFFF',
          borderLeft: '1px solid #E5E7EB',
        }
      }}
    >
      <Box sx={{ width: 280, pt: 2 }}>
        <List>
          {menuItems.map((item) => (
            <ListItem
              key={item.label}
              onClick={() => {
                navigate(item.path);
                setMobileMenuOpen(false);
              }}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(13, 148, 136, 0.06)',
                }
              }}
            >
              <ListItemText primary={item.label} primaryTypographyProps={{ color: 'text.primary' }} />
            </ListItem>
          ))}

          {!username ? (
            <>
              <ListItem
                onClick={() => {
                  navigate('/login');
                  setMobileMenuOpen(false);
                }}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemText primary={t('login')} primaryTypographyProps={{ color: 'primary.main' }} />
              </ListItem>
              <ListItem
                onClick={() => {
                  navigate('/register');
                  setMobileMenuOpen(false);
                }}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemText primary={t('register')} primaryTypographyProps={{ color: 'primary.main' }} />
              </ListItem>
            </>
          ) : (
            <>
              <ListItem
                onClick={() => {
                  navigate('/profile');
                  setMobileMenuOpen(false);
                }}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemText primary={t('profile')} />
              </ListItem>
              <ListItem
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemText primary={t('logout')} color="error" />
              </ListItem>
            </>
          )}
          <ListItem>
            <LanguageSwitcher />
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );

  // (v5 video-autoplay machinery removed with the marketing video backdrop)

  return (
    <>
      <CssBaseline />
      {/* Video Background */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -2,
          overflow: 'hidden',
        }}
      >
        {/* White-label: static gradient backdrop instead of the v5 marketing
            video (saves 15MB and keeps the tenant look calm/professional). */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(160deg, #F6F8FA 0%, #E9EFF4 55%, #DCE7EE 100%)',
          }}
        />
        {/* Dark overlay to ensure readability */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#FFFFFF',
            backdropFilter: 'none',
          }}
        />
      </Box>

      {/* Additional gradient overlay for visual depth */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -1,
          backgroundImage: 'none',
          backgroundColor: '#F8FAFC',
          pointerEvents: 'none',
        }}
      />

      {/* Light theme: no overlay needed */}

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ConcordChat />
        {/* Impersonation banner */}
        {localStorage.getItem('admin_original_token') && (
          <Box sx={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1301,
            bgcolor: '#7c3aed', color: '#fff', py: 0.5, px: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
            fontSize: '0.85rem', fontWeight: 600,
          }}>
            Viewing as: {localStorage.getItem('username')}
            <Button size="small" variant="outlined"
              sx={{ color: '#fff', borderColor: '#fff', textTransform: 'none', fontWeight: 700, py: 0, fontSize: '0.8rem' }}
              onClick={() => {
                const adminToken = localStorage.getItem('admin_original_token');
                const adminUsername = localStorage.getItem('admin_original_username');
                localStorage.setItem('token', adminToken);
                localStorage.setItem('username', adminUsername);
                localStorage.removeItem('admin_original_token');
                localStorage.removeItem('admin_original_username');
                window.location.href = '/admin';
              }}
            >
              Return to Admin
            </Button>
          </Box>
        )}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            borderBottom: '1px solid #F3F4F6',
            ...(localStorage.getItem('admin_original_token') ? { top: '32px' } : {}),
          }}
        >
          <Toolbar sx={{ px: { xs: 2, sm: 4 }, height: 70 }}>
            <Box
              component={Link}
              to="/projects"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                flexGrow: 1,
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: 0.85
                }
              }}
            >
              <img
                src="/logo512.png"
                alt="Qualitati"
                width={36}
                height={36}
                style={{
                  height: '36px',
                  marginRight: '8px',
                  filter: 'none',
                  transition: 'transform 0.3s ease',
                }}
              />
              <img
                src="/Qualitati.png"
                alt="Qualitati"
                style={{
                  height: '20px',
                  objectFit: 'contain',
                  marginRight: '6px',
                }}
              />
              {/* Co-brand lockup: QualiTaTi × Karrierehuset (white-label tenant) */}
              <Box
                component="span"
                sx={{ mx: 1, fontWeight: 400, opacity: 0.5, color: 'text.secondary' }}
              >
                ×
              </Box>
              <img
                src="/karrierehuset-logo.png"
                alt="Karrierehuset"
                style={{
                  height: '26px',
                  objectFit: 'contain',
                  // The PNG's swoosh sits above the wordmark, pushing the text
                  // optically low — lift it so both wordmarks share a baseline.
                  transform: 'translateY(-6px)',
                }}
              />
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {menuItems.map((item) => (
                  <NavButton
                    key={item.label}
                    component={Link}
                    to={item.path}
                  >
                    {item.label}
                  </NavButton>
                ))}

                {username ? (
                  <>
                    <IconButton
                      sx={{
                        color: 'primary.main',
                        '&:hover': { backgroundColor: 'rgba(13, 148, 136, 0.06)' }
                      }}
                      onClick={handleMenu}
                    >
                      <AccountCircleIcon />
                      <Typography variant="subtitle1" sx={{ ml: 1, color: 'text.primary', fontWeight: 500 }}>
                        {username}
                      </Typography>
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                      PaperProps={{
                        sx: {
                          mt: 1.5,
                          background: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          boxShadow: '0 10px 40px rgba(0,0,0,0.12), 0 2px 10px rgba(0,0,0,0.06)',
                          borderRadius: '12px',
                        }
                      }}
                    >
                      <MenuItem onClick={() => {
                        handleClose();
                        navigate('/profile');
                      }}>
                        {t('profile')}
                      </MenuItem>
                      <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                        {t('logout')}
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      component={Link}
                      to="/login"
                      sx={{ mr: 1 }}
                    >
                      {t('login')}
                    </Button>
                    <Button
                      variant="contained"
                      component={Link}
                      to="/register"
                      sx={{ mr: 1 }}
                    >
                      {t('register')}
                    </Button>
                  </>
                )}
                <LanguageSwitcher />
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                onClick={() => setMobileMenuOpen(true)}
                edge="end"
                aria-label="Open navigation menu"
                sx={{
                  width: 40,
                  height: 40,
                  color: 'primary.main'
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>

        {renderMobileMenu()}

        <Container
          component="main"
          maxWidth="xl"
          sx={{
            // Landing page bleeds edge-to-edge: only clear the fixed AppBar, no gap before the footer
            mt: isHomePage ? { xs: 8, md: 10 } : 12,
            mb: isHomePage ? 0 : { xs: 12, md: 4 }, // Increased bottom margin on mobile to avoid overlap with floating widgets (cookies/chat)
            flexGrow: 1,
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            // Override internal component styles for transparency
            '& .MuiPaper-root': {
              // Let the theme handle the glassmorphism
            },
          }}
        >
          {children}
        </Container>

        {/* Footer - Hidden on Interview pages */}
        {!isInterviewPage && <Footer />}
      </Box>
    </>
  );
};

export default MainLayout;
